import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, Save, Sprout, MoveDiagonal } from "lucide-react";
import { PageHeader, SectionDivider } from "@/components/primitives";
import { api, listPlants, updatePlant } from "@/lib/api";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STAGE_COLOR = {
  purchased: "#7E8C5B",
  planted: "#A4543A",
  germinated: "#5F6B43",
  flowering: "#C26B4A",
  dormant: "#8A8474",
  finished: "#5A5448",
};

const DEFAULT_WIDTH = 200;
const DEFAULT_LENGTH = 300;
const DEFAULT_SPACING = 30;

export default function BedPlanner() {
  const { id } = useParams();
  const nav = useNavigate();
  const [bed, setBed] = useState(null);
  const [plants, setPlants] = useState([]); // plants in this bed
  const [unplaced, setUnplaced] = useState([]); // user plants not yet in any bed
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: "", width_cm: "", length_cm: "", row_spacing_cm: "", notes: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/beds/${id}`);
      setBed(data.bed);
      setPlants(data.plants);
      setForm({
        name: data.bed.name || "",
        width_cm: data.bed.width_cm ?? "",
        length_cm: data.bed.length_cm ?? "",
        row_spacing_cm: data.bed.row_spacing_cm ?? "",
        notes: data.bed.notes ?? "",
      });
      const all = await listPlants();
      setUnplaced(all.filter((p) => !p.garden_bed_id));
    } catch {
      toast.error("Bed not found");
      nav("/beds");
    } finally {
      setLoading(false);
    }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  const saveBed = async () => {
    try {
      const payload = {
        name: form.name,
        width_cm: form.width_cm === "" ? null : Number(form.width_cm),
        length_cm: form.length_cm === "" ? null : Number(form.length_cm),
        row_spacing_cm: form.row_spacing_cm === "" ? null : Number(form.row_spacing_cm),
        notes: form.notes || null,
      };
      const { data } = await api.put(`/beds/${id}`, payload);
      setBed(data);
      setEditing(false);
      toast.success("Bed saved");
    } catch {
      toast.error("Could not save bed");
    }
  };

  const removeBed = async () => {
    try {
      await api.delete(`/beds/${id}`);
      toast.success("Bed removed");
      nav("/beds");
    } catch {
      toast.error("Could not remove");
    }
  };

  const addPlantToBed = async (plant) => {
    const w = bed.width_cm || DEFAULT_WIDTH;
    const l = bed.length_cm || DEFAULT_LENGTH;
    const spacing = bed.row_spacing_cm || DEFAULT_SPACING;
    // Place at next grid intersection inside the bed
    const used = new Set(plants.map((p) => `${Math.round((p.position_x ?? -1) / spacing)},${Math.round((p.position_y ?? -1) / spacing)}`));
    let target = { x: spacing, y: spacing };
    outer: for (let y = spacing; y <= l - spacing; y += spacing) {
      for (let x = spacing; x <= w - spacing; x += spacing) {
        if (!used.has(`${Math.round(x / spacing)},${Math.round(y / spacing)}`)) {
          target = { x, y };
          break outer;
        }
      }
    }
    try {
      const updated = await updatePlant(plant.id, {
        garden_bed_id: id,
        position_x: target.x,
        position_y: target.y,
      });
      setPlants((prev) => [...prev, updated]);
      setUnplaced((prev) => prev.filter((p) => p.id !== plant.id));
      toast.success(`${plant.name} placed in the bed`);
    } catch {
      toast.error("Could not place plant");
    }
  };

  const removePlantFromBed = async (plant) => {
    try {
      const updated = await updatePlant(plant.id, {
        garden_bed_id: null,
        position_x: null,
        position_y: null,
      });
      setPlants((prev) => prev.filter((p) => p.id !== plant.id));
      setUnplaced((prev) => [updated, ...prev]);
      toast.success(`${plant.name} lifted from this bed`);
    } catch {
      toast.error("Could not move plant");
    }
  };

  const updatePosition = useCallback(async (plantId, x, y) => {
    setPlants((prev) => prev.map((p) => (p.id === plantId ? { ...p, position_x: x, position_y: y } : p)));
    try {
      await updatePlant(plantId, { position_x: x, position_y: y });
    } catch {
      toast.error("Position not saved");
    }
  }, []);

  if (loading || !bed) {
    return (
      <div className="text-center mt-20 font-display italic text-ink-soft" data-testid="bedplanner-loading">
        Sketching the bed...
      </div>
    );
  }

  const widthCm = bed.width_cm || DEFAULT_WIDTH;
  const lengthCm = bed.length_cm || DEFAULT_LENGTH;
  const spacingCm = bed.row_spacing_cm || DEFAULT_SPACING;
  const cols = Math.max(1, Math.round(widthCm / spacingCm));
  const rows = Math.max(1, Math.round(lengthCm / spacingCm));

  return (
    <div data-testid="bedplanner-page">
      <div className="pt-6 flex items-center justify-between gap-2">
        <Link to="/beds" className="chip" data-testid="bedplanner-back">
          <ArrowLeft size={14} /> All beds
        </Link>
        <div className="flex gap-2">
          <button className="chip" onClick={() => setEditing((v) => !v)} data-testid="bedplanner-edit-toggle">
            <Pencil size={14} /> {editing ? "Close" : "Edit"}
          </button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button className="chip" data-testid="bedplanner-delete-trigger">
                <Trash2 size={14} className="text-terracotta" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-cream-soft border-heritage-border">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-display text-2xl text-sage-dark">Remove this bed?</AlertDialogTitle>
                <AlertDialogDescription className="text-ink-soft">
                  Plants in the bed will be lifted (kept in your garden, just unassigned).
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="btn-ghost" data-testid="bedplanner-delete-cancel">Keep it</AlertDialogCancel>
                <AlertDialogAction onClick={removeBed} className="btn-heritage" data-testid="bedplanner-delete-confirm">
                  Remove bed
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <PageHeader
        kicker="Bed Planner"
        title={bed.name}
        subtitle={`${widthCm}cm × ${lengthCm}cm · ${spacingCm}cm spacing`}
      />

      {editing && (
        <div className="h-card p-5 mb-4 animate-fade-up" data-testid="bedplanner-edit-form">
          <div className="kicker mb-3">Configure</div>
          <label className="block mb-3">
            <span className="text-sm text-ink-soft mb-1.5 inline-block">Bed name</span>
            <input className="field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="bedplanner-name" />
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-sm text-ink-soft mb-1.5 inline-block">Width (cm)</span>
              <input type="number" min="10" max="10000" className="field" value={form.width_cm}
                onChange={(e) => setForm({ ...form, width_cm: e.target.value })}
                placeholder={String(DEFAULT_WIDTH)} data-testid="bedplanner-width" />
            </label>
            <label className="block">
              <span className="text-sm text-ink-soft mb-1.5 inline-block">Length (cm)</span>
              <input type="number" min="10" max="10000" className="field" value={form.length_cm}
                onChange={(e) => setForm({ ...form, length_cm: e.target.value })}
                placeholder={String(DEFAULT_LENGTH)} data-testid="bedplanner-length" />
            </label>
            <label className="block">
              <span className="text-sm text-ink-soft mb-1.5 inline-block">Spacing (cm)</span>
              <input type="number" min="5" max="500" className="field" value={form.row_spacing_cm}
                onChange={(e) => setForm({ ...form, row_spacing_cm: e.target.value })}
                placeholder={String(DEFAULT_SPACING)} data-testid="bedplanner-spacing" />
            </label>
          </div>
          <label className="block mt-3">
            <span className="text-sm text-ink-soft mb-1.5 inline-block">Notes</span>
            <input className="field" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g., full sun, clay soil" data-testid="bedplanner-notes" />
          </label>
          <div className="mt-4 flex justify-end">
            <button onClick={saveBed} className="btn-heritage" data-testid="bedplanner-save">
              <Save size={14} /> Save bed
            </button>
          </div>
        </div>
      )}

      <BedCanvas
        widthCm={widthCm}
        lengthCm={lengthCm}
        spacingCm={spacingCm}
        cols={cols}
        rows={rows}
        plants={plants}
        onMove={updatePosition}
        onRemove={removePlantFromBed}
      />

      <div className="flex flex-wrap items-center justify-between gap-2 mt-6">
        <p className="text-sm text-ink-soft font-display italic">
          Drag plants to reposition. They snap to the {spacingCm}cm grid.
        </p>
        <Sheet>
          <SheetTrigger asChild>
            <button className="btn-heritage text-sm py-2 px-4" data-testid="bedplanner-add-trigger">
              <Plus size={14} /> Add plant to bed
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="bg-cream-soft border-heritage-border max-h-[80vh] overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="font-display text-2xl text-sage-dark">Place a plant here</SheetTitle>
              <SheetDescription className="text-ink-soft">
                Choose from plants not yet assigned to a bed.
              </SheetDescription>
            </SheetHeader>
            <div className="mt-4 space-y-2" data-testid="bedplanner-unplaced-list">
              {unplaced.length === 0 ? (
                <div className="text-center text-ink-soft font-display italic py-6">
                  Every plant is already placed. Add a new specimen from the Garden.
                </div>
              ) : (
                unplaced.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => addPlantToBed(p)}
                    className="h-card w-full p-3 flex items-center gap-3 hover:bg-cream-deep transition-colors text-left"
                    data-testid={`bedplanner-add-${p.id}`}
                  >
                    <span className="w-10 h-10 rounded-lg bg-cream-deep flex items-center justify-center">
                      <Sprout size={18} className="text-sage" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <div className="font-display text-xl text-sage-dark truncate">{p.name}</div>
                      <div className="text-xs text-ink-soft">{p.variety || p.color || p.stage}</div>
                    </span>
                    <Plus size={16} className="text-sage-dark" />
                  </button>
                ))
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <div className="mt-10">
        <SectionDivider label={`${plants.length} ${plants.length === 1 ? "plant" : "plants"} in this bed`} />
      </div>

      {plants.length === 0 ? (
        <p className="text-center font-display italic text-ink-soft mt-6" data-testid="bedplanner-empty">
          No plants in this bed yet. Add one to begin sketching.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-2 mt-4" data-testid="bedplanner-plant-chips">
          {plants.map((p) => (
            <Link
              key={p.id}
              to={`/garden/${p.id}`}
              className="h-card p-3 flex items-center gap-2"
              data-testid={`bedplanner-plant-${p.id}`}
            >
              <span className="inline-block w-3 h-3 rounded-full shrink-0" style={{ background: STAGE_COLOR[p.stage] }} />
              <span className="font-display text-lg text-ink truncate">{p.name}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function BedCanvas({ widthCm, lengthCm, spacingCm, cols, rows, plants, onMove, onRemove }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [dragId, setDragId] = useState(null);
  // Maintain landscape-or-portrait responsive: scale so longest side fits.
  // SVG viewBox in cm with padding for label area
  const PAD = Math.max(spacingCm, 20);

  const vbW = widthCm + PAD * 2;
  const vbH = lengthCm + PAD * 2;

  // Build grid lines
  const verticalLines = useMemo(() => {
    const out = [];
    for (let i = 0; i <= cols; i++) {
      const x = i * spacingCm;
      out.push(<line key={`v${i}`} x1={x} y1={0} x2={x} y2={lengthCm} stroke="#DDD3B8" strokeDasharray="2 4" strokeWidth="0.6" />);
    }
    return out;
  }, [cols, spacingCm, lengthCm]);

  const horizontalLines = useMemo(() => {
    const out = [];
    for (let i = 0; i <= rows; i++) {
      const y = i * spacingCm;
      out.push(<line key={`h${i}`} x1={0} y1={y} x2={widthCm} y2={y} stroke="#DDD3B8" strokeDasharray="2 4" strokeWidth="0.6" />);
    }
    return out;
  }, [rows, spacingCm, widthCm]);

  const svgPointToCm = (clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const transformed = pt.matrixTransform(ctm.inverse());
    return { x: transformed.x - PAD, y: transformed.y - PAD };
  };

  const onPointerDown = (e, p) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragId(p.id);
  };

  const onPointerMove = (e) => {
    if (!dragId) return;
    const { x, y } = svgPointToCm(e.clientX, e.clientY);
    const clampedX = Math.max(0, Math.min(widthCm, x));
    const clampedY = Math.max(0, Math.min(lengthCm, y));
    onMove(dragId, clampedX, clampedY);
  };

  const onPointerUp = (e) => {
    if (!dragId) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    // Snap to nearest grid intersection
    const p = plants.find((pp) => pp.id === dragId);
    if (p) {
      const snappedX = Math.round((p.position_x || 0) / spacingCm) * spacingCm;
      const snappedY = Math.round((p.position_y || 0) / spacingCm) * spacingCm;
      const clampedX = Math.max(0, Math.min(widthCm, snappedX));
      const clampedY = Math.max(0, Math.min(lengthCm, snappedY));
      onMove(dragId, clampedX, clampedY);
    }
    setDragId(null);
  };

  return (
    <div ref={containerRef} className="h-card overflow-hidden shadow-card animate-fade-up" data-testid="bed-canvas">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${vbW} ${vbH}`}
        className="w-full h-auto bg-cream-soft"
        style={{ touchAction: "none", maxHeight: "70vh" }}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {/* Outer label/scale ruler */}
        <text x={PAD + widthCm / 2} y={PAD - 6} textAnchor="middle" fontSize="10" fill="#5F6B43" fontFamily="Inter">
          {widthCm} cm
        </text>
        <text x={PAD - 6} y={PAD + lengthCm / 2} textAnchor="middle" fontSize="10" fill="#5F6B43" fontFamily="Inter"
              transform={`rotate(-90 ${PAD - 6} ${PAD + lengthCm / 2})`}>
          {lengthCm} cm
        </text>

        {/* Bed area */}
        <g transform={`translate(${PAD} ${PAD})`}>
          <rect x={0} y={0} width={widthCm} height={lengthCm} rx={Math.min(widthCm, lengthCm) * 0.02}
                fill="#F5EFDF" stroke="#5F6B43" strokeWidth="1.2" />
          {verticalLines}
          {horizontalLines}

          {/* Plants */}
          {plants.map((p) => {
            const cx = p.position_x ?? widthCm / 2;
            const cy = p.position_y ?? lengthCm / 2;
            const r = Math.max(spacingCm * 0.4, 10);
            const color = STAGE_COLOR[p.stage] || "#7E8C5B";
            const initial = (p.name || "?").trim().charAt(0).toUpperCase();
            return (
              <g
                key={p.id}
                transform={`translate(${cx} ${cy})`}
                style={{ cursor: dragId === p.id ? "grabbing" : "grab" }}
                onPointerDown={(e) => onPointerDown(e, p)}
                data-testid={`bed-node-${p.id}`}
              >
                <circle r={r} fill={color} stroke="#FBF7EC" strokeWidth="1.2" opacity={dragId === p.id ? 0.85 : 1} />
                <text textAnchor="middle" dy="0.35em" fontSize={r * 0.9} fill="#FBF7EC"
                      fontFamily="Cormorant Garamond, serif" fontWeight="700">
                  {initial}
                </text>
                <title>{p.name} — drag to move, double-tap to lift</title>
              </g>
            );
          })}
        </g>
      </svg>

      <div className="px-4 py-3 text-xs text-ink-soft flex items-center gap-2 flex-wrap border-t border-heritage-border">
        <MoveDiagonal size={12} />
        <span>Pinch is disabled — the bed scales to fit. Use Edit to change size.</span>
      </div>
    </div>
  );
}
